import React from 'react';
import { Modal } from 'antd';
import { Check } from 'lucide-react';
import { Button } from '@/components/Button';
import styles from './NextPartModal.module.scss';

interface NextPartModalProps {
  /** e.g. "Competency questionnaire complete" */
  title: string;
  /** One line under the title — what just happened on this page. */
  body: string;
  finishedLabel: string;
  finishedDetail: string;
  nextLabel: string;
  nextDetail: string;
  /** Whether the next part has never been started — shows a "Not started" tag next to its title. */
  nextNotStarted?: boolean;
  /** e.g. "Start HTS" / "Resume Competency" */
  primaryLabel: string;
  /** e.g. "View results" */
  dismissLabel: string;
  onPrimary: () => void;
  onDismiss: () => void;
}

/**
 * Shown once when a candidate finishes one questionnaire of a template that
 * still has the other one open, so the next step is spelled out at the moment
 * it matters instead of leaving them to find it back on Sessions. Same chrome
 * as ExitSessionModal.
 */
export const NextPartModal: React.FC<NextPartModalProps> = ({
  title,
  body,
  finishedLabel,
  finishedDetail,
  nextLabel,
  nextDetail,
  nextNotStarted,
  primaryLabel,
  dismissLabel,
  onPrimary,
  onDismiss,
}) => (
  <Modal open onCancel={onDismiss} footer={null} width={440} centered title={null}>
    <h3 className={styles.title}>{title}</h3>
    <p className={styles.body}>{body}</p>
    <ul className={styles.parts} aria-label="This interview's questionnaires">
      <li className={styles.part}>
        <span className={`${styles.mark} ${styles.markDone}`} aria-hidden="true">
          <Check size={12} strokeWidth={3} />
        </span>
        <span className={styles.partText}>
          <span className={styles.partName}>{finishedLabel}</span>
          <span className={styles.partDetail}>{finishedDetail}</span>
        </span>
      </li>
      <li className={styles.part}>
        <span className={styles.mark} aria-hidden="true" />
        <span className={styles.partText}>
          <span className={styles.partNameRow}>
            <span className={styles.partName}>{nextLabel}</span>
            {nextNotStarted && <span className={styles.statusTag}>Not started</span>}
          </span>
          <span className={styles.partDetail}>{nextDetail}</span>
        </span>
      </li>
    </ul>
    <div className={styles.actions}>
      <Button appearance="outline" onClick={onPrimary}>
        {primaryLabel}
      </Button>
      <Button appearance="solid" accent="green" onClick={onDismiss}>
        {dismissLabel}
      </Button>
    </div>
  </Modal>
);

export default NextPartModal;
